import { ContainerErrorType } from '@fluidframework/container-definitions';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { OWHErrorType } from '@fluidx/office-fluid-types';
import { OdspErrorType } from '@fluidframework/odsp-driver-definitions';
import {
  Locale,
  tryMatchContainerErrorType,
  tryMatchDriverErrorType,
  tryMatchOdspDriverErrorType,
  tryMatchOWHErrorType
} from '@fluidx/utilities';
import { getStringTable } from '../../../utilities/localizationUtilities';
import { HostUxStrings } from '../../../stringTypes/HostUxStrings';
import { FluidErrorInfo, UnknownError } from '../ErrorInfo';
import { MessageBannerStrings } from './MessageBanner';

export interface BannerErrorInfo extends FluidErrorInfo {
  bannerStrings: MessageBannerStrings;
}

/**
 * Inspects a given error to determine potential fluid error type and
 * select appropriate localized string for banner error ux
 * @param error driver, container or office web host error
 * @param locale Host locale
 * @param useOdspErrors
 * @returns Promise to BannerErrorInfo
 */
export async function getBannerMessagingForError(error: unknown, locale: Locale): Promise<BannerErrorInfo> {
  const stringTable = await getStringTable(locale);

  const defaultBannerErrorInfo: BannerErrorInfo = {
    errorType: UnknownError,
    bannerStrings: {
      bannerTitle: stringTable.criticalErrorBannerTitle,
      bannerMessage: stringTable.criticalErrorBannerMessage
    }
  };

  const bannerErrorInfo: BannerErrorInfo =
    getBannerMessagingForContainerError(error, stringTable) ||
    getBannerMessagingForOdspDriverError(error, stringTable) ||
    getBannerMessagingForBaseDriverError(error, stringTable) ||
    defaultBannerErrorInfo;

  return Promise.resolve(bannerErrorInfo);
}

/**
 * Select appropriate localized string for container errors
 */
function getBannerMessagingForContainerError(error: unknown, stringTable: HostUxStrings): BannerErrorInfo | undefined {
  const errorType = tryMatchContainerErrorType(error) || tryMatchOWHErrorType(error);

  if (!errorType) {
    return;
  }

  switch (errorType) {
    case OWHErrorType.ContainerUpgradeRequired:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.containerUpgradeRequiredBannerTitle,
          bannerMessage: stringTable.containerUpgradeRequiredBannerMessage
        }
      };

    case ContainerErrorType.throttlingError:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.genericServiceBusyBannerTitle,
          bannerMessage: stringTable.genericServiceBusyBannerMessage
        }
      };

    case ContainerErrorType.genericError:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.criticalErrorBannerTitle,
          bannerMessage: stringTable.criticalErrorBannerMessage
        }
      };

    default:
      return;
  }
}

/**
 * Select appropriate localized string for base driver errors
 */
function getBannerMessagingForBaseDriverError(error: unknown, stringTable: HostUxStrings): BannerErrorInfo | undefined {
  const errorType = tryMatchDriverErrorType(error);

  if (!errorType) {
    return;
  }

  switch (errorType) {
    case DriverErrorType.fileNotFoundOrAccessDeniedError:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.fileNotFoundOrAccessDeniedBannerTitle,
          bannerMessage: stringTable.fileNotFoundOrAccessDeniedBannerMessage
        }
      };

    case DriverErrorType.authorizationError:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.authorizationErrorBannerTitle,
          bannerMessage: stringTable.authorizationErrorBannerMessage
        }
      };

    case DriverErrorType.throttlingError:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.genericServiceBusyBannerTitle,
          bannerMessage: stringTable.genericServiceBusyBannerMessage
        }
      };

    case DriverErrorType.genericError:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.criticalErrorBannerTitle,
          bannerMessage: stringTable.criticalErrorBannerMessage
        }
      };

    default:
      return;
  }
}

/**
 * Select appropriate localized string for Odsp specific driver errors
 */
function getBannerMessagingForOdspDriverError(error: unknown, stringTable: HostUxStrings): BannerErrorInfo | undefined {
  const errorType = tryMatchOdspDriverErrorType(error);

  if (!errorType) {
    return;
  }

  switch (errorType) {
    case DriverErrorType.fileOverwrittenInStorage:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.odspVersionMismatchBannerTitle,
          bannerMessage: stringTable.odspVersionMismatchBannerMessage
        }
      };

    case OdspErrorType.outOfStorageError:
      return {
        errorType,
        bannerStrings: {
          bannerTitle: stringTable.odspOutOfStorageBannerTitle,
          bannerMessage: stringTable.odspOutOfStorageBannerMessage
        }
      };

    // TBD: Define error messaging for these Odsp error cases
    case OdspErrorType.invalidFileNameError:
    case OdspErrorType.snapshotTooBig:
    case OdspErrorType.fetchTimeout:
    case OdspErrorType.fluidNotEnabled:

    default:
      return;
  }
}

/**
 * Select appropriate localized string for banner
 * @param containerState non-error container state
 * @param locale Host locale
 * @returns Promise to MessageBannerStrings
 */
export async function getBannerMessagingForContainerState(
  containerState: 'Disconnected' | 'Readonly' | 'ReadonlyMobile',
  locale: Locale
): Promise<MessageBannerStrings> {
  const stringTable = await getStringTable(locale);

  switch (containerState) {
    case 'Disconnected':
      return {
        bannerTitle: stringTable.disconnectedStateBannerTitle,
        bannerMessage: stringTable.disconnectedStateBannerMessage
      };

    case 'Readonly':
      return {
        bannerTitle: stringTable.readonlyStateBannerTitle,
        bannerMessage: stringTable.readonlyStateBannerMessage
      };

    case 'ReadonlyMobile':
      return {
        bannerTitle: stringTable.readonlyStateBannerTitle,
        bannerMessage: stringTable.readonlyStateMobileBannerMessage
      };
  }
}
